import { Button } from "@/src/components/buttons";
import { closeDialog, openDialog } from "@/src/components/dialogs";
import apiFetch from "@/src/utils/apiFetch";
import { useEffect, useRef, useState } from "react";
import { LiveAudioVisualizer } from "react-audio-visualize";
import { openAudioTranscription } from "./audioTranscriptionDialog";

async function openAudioRecorder({
  shouldShowFooter = true,
  onChange,
}: {
  shouldShowFooter?: boolean;
  onChange: (transcript: string, base64Audio: string | null) => void;
}) {
  let base64Audio: string | null = null;
  await openDialog({
    id: "audioRecorder",
    title: "Reconnaissance vocale",
    content: (
      <AudioRecorder
        shouldShowFooter={shouldShowFooter}
        onBase64Change={(newBase64) => {
          base64Audio = newBase64;
        }}
        onConfirm={async (newBase64) => {
          base64Audio = newBase64;
          console.log("the base64Audio", newBase64);
          
          onChange("", base64Audio);
          closeDialog("audioRecorder");
          // await openAudioTranscription({ base64Audio, onChange });
          // closeDialog("audioRecorder");
        }}
      />
    ),
  });
}

function AudioRecorder({
  shouldShowFooter = true,
  onBase64Change,
  onConfirm,
}: {
  shouldShowFooter?: boolean;
  onBase64Change: (base64Audio: string) => void;
  onConfirm: (base64Audio: string) => void;
}) {
  const [base64Audio, setBase64Audio] = useState("");
  const [isRecording, setIsRecording] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  useEffect(() => {
    startRecording();
    return () => {
      stopRecording();
    };
  }, []);

  const startRecording = async () => {
    try {
      // Request permission to use the microphone
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      mediaRecorderRef.current = new MediaRecorder(stream);
      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setErrorMessage(""); // Clear any previous errors
    } catch (error: any) {
      if (
        error.name === "NotAllowedError" ||
        error.name === "PermissionDeniedError"
      ) {
        setErrorMessage(
          "Microphone access was denied. Please allow microphone access to record audio."
        );
      } else {
        setErrorMessage("An error occurred while accessing the microphone.");
      }
      console.error("Error accessing microphone:", error);
    }
  };

  const stopRecording = async (): Promise<string> => {
    return new Promise((resolve, reject) => {
      if (!mediaRecorderRef.current) return;
      mediaRecorderRef.current.stop();
      streamRef.current?.getTracks().forEach((track) => track.stop());
      setIsRecording(false);
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: "audio/wav",
        });
        audioChunksRef.current = [];

        const reader = new FileReader();
        reader.readAsDataURL(audioBlob);
        reader.onloadend = async () => {
          const base64 = reader.result?.toString();
          if (!base64) {
            setErrorMessage("No base64 data");
            return reject(new Error("No base64 data"));
          }
          setBase64Audio(base64);

          onBase64Change(base64);
          resolve(base64);
        };
      };
    });
  };

  return (
    <div className="c1 gap10">
      {isRecording && (
        <div className="center gray p20 c1 gap20">
          <div>Reconnaissance vocale en cours...</div>
          {!shouldShowFooter && (
            <div className="bold">
              Lâchez la touche F4 pour arrêter l&apos;enregistrement
            </div>
          )}
        </div>
      )}
      <div className="center">
        {mediaRecorderRef.current && (
          <LiveAudioVisualizer
            mediaRecorder={mediaRecorderRef.current}
            barWidth={5}
            width={350}
            gap={3}
          />
        )}
      </div>

      {errorMessage && <p style={{ color: "red" }}>{errorMessage}</p>}

      {base64Audio && (
        <div>
          <h3>Recorded Audio:</h3>
          <audio controls src={base64Audio}></audio>
        </div>
      )}

      {shouldShowFooter && (
        <div className="c3 gap20 ">
          <Button
            onClick={async () => {
              await stopRecording();
              closeDialog("audioRecorder");
            }}
          >
            Annuler
          </Button>
          <div></div>
          <Button
            onClick={async () => {
              const base64 = await stopRecording();
              if (!base64) return;
              onConfirm(base64);
            }}
          >
            Suivant
          </Button>
        </div>
      )}
    </div>
  );
}

export { AudioRecorder, openAudioRecorder };
